import { z } from 'zod';

export type SerializedMap<M> = M extends Map<infer K, infer V>
  ? {
      _dataType: 'Map';
      value: [K, V][];
    }
  : never;

export type SerializedSet<T extends Set<unknown>> = {
  _dataType: 'Set';
  value: T extends Set<infer U> ? U[] : never;
};

type SerializedValue<T> = T extends Map<unknown, unknown>
  ? SerializedMap<T>
  : T extends Set<unknown>
  ? SerializedSet<T>
  : T;

function serializerWithMaps<T>(_key: unknown, value: T): SerializedValue<T> {
  if (value instanceof Map) {
    return {
      _dataType: 'Map',
      value: Array.from(value.entries()),
    } as SerializedValue<T>;
  } else if (value instanceof Set) {
    return {
      _dataType: 'Set',
      value: Array.from(value.values()),
    } as SerializedValue<T>;
  } else {
    return value as SerializedValue<T>;
  }
}

const isSerializedMapOrSet = (
  o: unknown,
): o is SerializedMap<Map<unknown, unknown>> | SerializedSet<Set<unknown>> => {
  return (
    typeof o === 'object' &&
    o !== null &&
    '_dataType' in o &&
    'value' in o &&
    (o._dataType === 'Map' || o._dataType === 'Set')
  );
};

function parserWithMaps<T>(_key: unknown, value: SerializedValue<T>): T {
  if (isSerializedMapOrSet(value)) {
    if (value._dataType === 'Map') {
      return new Map(value.value) as T;
    } else if (value._dataType === 'Set') {
      return new Set(value.value) as T;
    }
  }
  return value as T;
}

/**
 * JSON.parse but able to transform tagged objects into Map or Set instances. Not type safe, assumes the parsed data type is correct.
 */
export function parseWithMaps<T>(data: string): T {
  return JSON.parse(data, parserWithMaps);
}

/**
 * JSON.stringify but turns Map and Set instances into tagged objects
 */
export function serializeWithMaps<T>(data: T): string {
  return JSON.stringify(data, serializerWithMaps);
}

// Simple type to represent any validation error structure
type ZodLikeError = {
  message?: string;
  errors?: Array<{
    message: string;
    path?: (string | number)[];
  }>;
};

// Type to handle both z.ZodType and zod.ZodType imports
type ZodSchema<T = unknown> = {
  safeParse: (
    data: unknown,
  ) => { success: true; data: T } | { success: false; error: ZodLikeError };
};

/**
 * Formats a Zod-like error with a custom message prefix
 */
function formatZodLikeError(message: string, error: ZodLikeError): string {
  // If error has detailed issues, format them
  if (error.errors && error.errors.length > 0) {
    const issues = error.errors
      .map((err) => {
        const pathStr =
          err.path && err.path.length > 0 ? ` (at ${err.path.join('.')})` : '';
        return `${err.message}${pathStr}`;
      })
      .join(', ');
    return `${message}: ${issues}`;
  }

  return `${message}: ${error.message || 'Validation failed'}`;
}

/**
 * A helper function that combines Zod's safeParse with custom error messaging
 *
 * @param schema The Zod schema to validate against
 * @param data The data to validate
 * @param errorMessage A custom error message prefix
 * @returns The parsed and typed data
 * @throws Error with custom message if validation fails
 */
export function parseWithMessage<T>(
  schema: ZodSchema<T>,
  data: unknown,
  errorMessage: string,
): T {
  const result = schema.safeParse(data);

  if (!result.success) {
    // Format the error message to include both custom message and validation details
    const formattedError = formatZodLikeError(errorMessage, result.error);
    throw new Error(formattedError);
  }

  return result.data;
}

/** Parse JSON. Instead of throwing, returns a fallback value on parse errors. */
export function safeParseJSON(data: string, errorValue = undefined): unknown {
  try {
    return JSON.parse(data);
  } catch (e) {
    return errorValue;
  }
}
